/**
 * Below-the-fold editorial sections.
 * Mirror the existing PDF brand system: eyebrow + short orange rule,
 * Fraunces display, Inter Tight UI, generous margins.
 */
import { useEffect, useState, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LAYERS = [
  {
    n: "01",
    title: "Clarity",
    body: "Define the real question. Most bad decisions start fuzzy.",
    prompt: "What exactly am I deciding?",
  },
  {
    n: "02",
    title: "Consequence",
    body: "Map the outcomes. Best, worst, most likely. Most people only see the first.",
    prompt: "What are the best and worst cases?",
  },
  {
    n: "03",
    title: "Reversibility",
    body: "Some doors lock behind you. Reversible decisions deserve speed. Irreversible ones deserve patience.",
    prompt: "How hard is it to reverse?",
  },
  {
    n: "04",
    title: "Timing",
    body: "A right call at the wrong moment becomes a bad call. Ask if now is really the time.",
    prompt: "Is this the right moment?",
  },
];

export const HowItWorks = () => (
  <section className="ed ed--how" data-testid="how-it-works">
    <div className="ed__inner">
      <div className="ed__head">
        <span className="ed__eyebrow">HOW IT WORKS</span>
        <span className="ed__rule" />
        <h2 className="ed__title">
          Four layers.
          <br />
          <span className="ed__title-accent">Sixteen questions.</span>
        </h2>
        <p className="ed__deck">
          Run any important decision through these in order. Don't move on
          until you've written a clear answer.
        </p>
      </div>

      <div className="ed__grid" data-testid="layers-grid">
        {LAYERS.map((l, i) => (
          <article
            key={l.n}
            className={`layer-card${i % 2 === 1 ? " layer-card--cream" : ""}`}
            data-testid={`layer-card-${l.n}`}
          >
            <span className="layer-card__bar" />
            <div className="layer-card__head">
              <span className="layer-card__num">{l.n}</span>
              <span className="layer-card__title">{l.title}</span>
            </div>
            <p className="layer-card__body">{l.body}</p>
            <div className="layer-card__prompt">
              <span className="layer-card__prompt-label">ASK YOURSELF</span>
              <span className="layer-card__prompt-text">{l.prompt}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// --------- 92% stat block (with simple count-up on scroll into view) ---------
const useInView = (ref, threshold = 0.4) => {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setV(true);
      },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return v;
};

export const StatBlock = () => {
  const ref = useRef(null);
  const seen = useInView(ref, 0.35);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!seen) return;
    let raf, start;
    const dur = 1500;
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(eased * 92));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [seen]);

  return (
    <section className="ed ed--stat" ref={ref} data-testid="stat-block">
      <div className="ed__inner">
        <div className="stat__row">
          <div className="stat__left">
            <span className="ed__eyebrow">THE NUMBER</span>
            <span className="ed__rule" />
            <h2 className="stat__big">
              {n}
              <span className="stat__pct">%</span>
            </h2>
            <p className="stat__cap">
              of strategic decisions fail to achieve their intended outcomes.
            </p>
            <p className="stat__src">
              Source: Harvard Business School research, 2023.
            </p>
          </div>
          <div className="stat__right">
            <p className="stat__quote">
              “Most bad decisions don't fail from a single oversight. They fail
              because thinkers skip essential layers of analysis.”
            </p>
            <span className="stat__attrib">— From the Insight Guide</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// --------- Products grid (re-uses your existing 6 downloads + brand kit) ---------
const PRODUCTS = [
  {
    slug: "brand-kit",
    eyebrow: "★ Brand kit",
    title: "Complete Brand Kit",
    sub: "Logos, 17 social templates, brand book, captions. One ZIP.",
    meta: "ZIP · 35 files · 1.4 MB",
    type: "zip",
    featured: true,
    fname: "Happy_Peppers_Brand_Kit.zip",
  },
  {
    slug: "decision-filter",
    eyebrow: "01 / Workbook",
    title: "Practical Checklist",
    sub: "7-page fillable workbook with 48 form fields. The whole framework.",
    meta: "PDF · 7 pages · 124 KB",
    type: "pdf",
    fname: "Happy_Peppers_Decision_Filter.pdf",
  },
  {
    slug: "frame",
    eyebrow: "02 / Poster",
    title: "Frame Design",
    sub: "Editorial infographic — the 4-layer funnel + 92% hero stat.",
    meta: "PDF · 1 page · 73 KB",
    type: "pdf",
    fname: "Frame_Design_Happy_Peppers.pdf",
  },
  {
    slug: "onepager",
    eyebrow: "03 / Summary",
    title: "One-Pager",
    sub: "Shareable summary card — 4 layer cards with prompts.",
    meta: "PDF · 1 page · 73 KB",
    type: "pdf",
    fname: "One_Pager_Happy_Peppers.pdf",
  },
  {
    slug: "insight",
    eyebrow: "04 / Long read",
    title: "Insight Guide",
    sub: "3-page editorial article with pull quotes, stats, references.",
    meta: "PDF · 3 pages · 87 KB",
    type: "pdf",
    fname: "Insight_Guide_Happy_Peppers.pdf",
  },
  {
    slug: "brand-guidelines",
    eyebrow: "05 / Brand book",
    title: "Brand Guidelines",
    sub: "10-page master document — logo, colour, type, voice, do's & don'ts.",
    meta: "PDF · 10 pages · 94 KB",
    type: "pdf",
    fname: "Happy_Peppers_Brand_Guidelines.pdf",
  },
  {
    slug: "notion",
    eyebrow: "06 / Template",
    title: "Notion Template",
    sub: "Drop-in markdown with callouts, checkboxes and a database schema.",
    meta: "Markdown · 6 KB",
    type: "md",
    fname: "Notion_Template_4Layer_Decision_Filter.md",
  },
];

const ProductCard = ({ p }) => {
  const href = `${BACKEND_URL}/api/download/${p.slug}`;
  const handleClick = (e) => {
    e.preventDefault();
    window.location.assign(href);
  };
  return (
    <a
      href={href}
      download={p.fname}
      onClick={handleClick}
      className={`pc${p.featured ? " pc--featured" : ""}`}
      data-testid={`product-card-${p.slug}`}
    >
      <div className="pc__top">
        <span className="pc__eyebrow">{p.eyebrow}</span>
        <span className={`pc__pill pc__pill--${p.type}`}>
          {p.type.toUpperCase()}
        </span>
      </div>
      <h3 className="pc__title">{p.title}</h3>
      <p className="pc__sub">{p.sub}</p>
      <div className="pc__bottom">
        <span className="pc__meta">{p.meta}</span>
        <span className="pc__cta">
          Download
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="m6 13 6 6 6-6" />
          </svg>
        </span>
      </div>
    </a>
  );
};

export const Products = () => (
  <section className="ed ed--products" data-testid="products-section">
    <div className="ed__inner">
      <div className="ed__head">
        <span className="ed__eyebrow">THE LIBRARY</span>
        <span className="ed__rule" />
        <h2 className="ed__title">
          Everything,
          <br />
          <span className="ed__title-accent">free.</span>
        </h2>
        <p className="ed__deck">
          Seven pieces. One framework. Tap any card to download — no email,
          no tricks.
        </p>
      </div>
      <div className="pc__grid">
        {PRODUCTS.map((p) => (
          <ProductCard key={p.slug} p={p} />
        ))}
      </div>
    </div>
  </section>
);

export const QuoteBlock = () => (
  <section className="ed ed--quote" data-testid="quote-block">
    <div className="ed__inner">
      <span className="ed__eyebrow">ONE LINE PRINCIPLE</span>
      <p className="quote__text">
        “Good decisions come from full thinking, not fast thinking.”
      </p>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="ed__footer" data-testid="site-footer">
    <div className="ed__inner">
      <div className="ed__footer-row">
        <div>
          <span className="ed__brand">Decision Engine</span>
          <span className="ed__footer-sub">by Happy Peppers</span>
        </div>
        <span className="ed__copy">© 2026  ·  A FRAME FOR THINKING</span>
      </div>
    </div>
  </footer>
);
